import { useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { Sparkle } from "@/components/site/Sparkle";
import { IMG, ADDRESS, PHONE, PHONE_TEL, HOURS, HAIR_SERVICES, LASER_SERVICES, BOOKING_URL } from "@/data/site";
import { WEB3FORMS_ENDPOINT, WEB3FORMS_ACCESS_KEY } from "@/config/form";

const inputCls =
  "w-full bg-transparent border border-stone focus:border-gold focus:ring-1 focus:ring-gold outline-none px-5 py-4 font-sans text-sm text-espresso placeholder:text-deep/40 tracking-wide transition-colors duration-300";

const labelCls =
  "block font-sans uppercase tracking-[0.2em] text-[11px] font-medium text-deep/70 mb-2.5";

const errCls = "mt-2 font-sans text-xs tracking-wide text-[#9C6B3D]";

// Driven from the same data source as the Services pages so they can never drift apart.
const SERVICE_GROUPS = [
  { label: "Hair", options: HAIR_SERVICES.map((s) => `Hair — ${s.name}`) },
  { label: "Laser & Skin", options: LASER_SERVICES.map((s) => `Laser & Skin — ${s.name}`) },
];

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Next available day: tomorrow in the visitor's local time; if tomorrow is a
// Tuesday (the studio is closed), roll forward to Wednesday.
const nextAvailableDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  if (d.getDay() === 2) d.setDate(d.getDate() + 1);
  return toLocalISO(d);
};

const isTuesday = (iso) => iso && new Date(`${iso}T12:00:00`).getDay() === 2;

const Req = () => (
  <span className="text-gold" aria-hidden="true"> *</span>
);

const FIELD_IDS = {
  name: "bk-name",
  phone: "bk-phone",
  preferred_date: "bk-date",
  service: "bk-service",
  email: "bk-email",
};
const FIELD_ORDER = ["name", "phone", "preferred_date", "service", "email"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferred_date: nextAvailableDate(),
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const botcheckRef = useRef(null); // honeypot — real visitors never check it

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const validate = (f) => {
    const errs = {};
    if (!f.name.trim()) errs.name = "Please enter your full name.";
    const digits = f.phone.replace(/\D/g, "");
    if (!f.phone.trim()) errs.phone = "Please enter your phone number.";
    else if (digits.length < 10 || digits.length > 11)
      errs.phone = "Please enter a valid phone number (at least 10 digits).";
    if (!f.preferred_date) errs.preferred_date = "Please choose a preferred date.";
    else if (isTuesday(f.preferred_date))
      errs.preferred_date = "We're closed on Tuesdays — please choose another day.";
    if (!f.service) errs.service = "Please select a service.";
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
      errs.email = "Please enter a valid email address.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    const first = FIELD_ORDER.find((k) => errs[k]);
    if (first) {
      const el = document.getElementById(FIELD_IDS[first]);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
      return;
    }
    // Simple throttle: one submission per browser every 30 seconds.
    const now = Date.now();
    const last = Number(localStorage.getItem("sz-last-enquiry-at") || 0);
    if (now - last < 30000) {
      toast.error("Please wait a few seconds before sending another request.");
      return;
    }
    setSending(true);
    setSendFailed(false);
    localStorage.setItem("sz-last-enquiry-at", String(now));
    // Direct browser-to-Web3Forms submission — no backend. Email is included
    // only when the visitor provided one (the optional field stays optional).
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: "New Enquiry — S & Z Hair and Laser Website",
      from_name: "S & Z Website",
      name: form.name.trim(),
      phone: form.phone.trim(),
      service: form.service,
      preferred_date: form.preferred_date,
      message: form.message.trim(),
      botcheck: botcheckRef.current?.checked || false,
    };
    if (form.email.trim()) payload.email = form.email.trim();
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error();
      setSent(true);
      toast.success("Your request has been received — we will be in touch shortly.");
    } catch {
      setSendFailed(true);
      toast.error("Something went wrong — please try again or call the studio.");
    } finally {
      setSending(false);
    }
  };

  const tuesdayNote = isTuesday(form.preferred_date);

  return (
    <>
      <PageHero
        testId="contact-hero"
        eyebrow="Contact & Book Now"
        titleLines={["Reserve", "Your Moment"]}
        quote="Private consultations available — tell us what you're dreaming of."
        image={IMG.spa4}
        imageAlt="A quiet corner of the S & Z Hair and Laser studio in Brampton"
      />

      <section data-testid="contact-main" className="bg-ivory py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>Booking Request</Eyebrow>
              <h2 className="font-display text-4xl sm:text-5xl text-espresso mt-4 mb-10">
                Begin With a <span className="italic text-gold">Conversation</span>
              </h2>
            </Reveal>

            {sent ? (
              <Reveal>
                <div
                  data-testid="booking-success-message"
                  className="border border-gold bg-ivory p-12 text-center img-frame"
                >
                  <Sparkle size={22} className="mx-auto twinkle" />
                  <h3 className="font-display text-3xl text-espresso mt-6">
                    Received, With Thanks
                  </h3>
                  <p className="font-quote italic text-xl text-deep/75 mt-4">
                    Your request is with the studio. Expect a personal reply
                    within one business day to confirm your appointment.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form data-testid="booking-form" onSubmit={submit} noValidate className="space-y-7">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bk-name" className={labelCls}>Full Name<Req /></label>
                      <input id="bk-name" data-testid="booking-name-input" autoComplete="name" aria-invalid={!!errors.name} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                      {errors.name && <p data-testid="booking-name-error" className={errCls}>{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="bk-email" className={labelCls}>Email (optional)</label>
                      <input id="bk-email" data-testid="booking-email-input" type="email" autoComplete="email" aria-invalid={!!errors.email} value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                      {errors.email && <p data-testid="booking-email-error" className={errCls}>{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="bk-phone" className={labelCls}>Phone<Req /></label>
                      <input id="bk-phone" data-testid="booking-phone-input" type="tel" inputMode="tel" autoComplete="tel" aria-invalid={!!errors.phone} value={form.phone} onChange={set("phone")} placeholder="Your best contact number" className={inputCls} />
                      {errors.phone && <p data-testid="booking-phone-error" className={errCls}>{errors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="bk-date" className={labelCls}>Preferred Date<Req /></label>
                      <input id="bk-date" data-testid="booking-date-input" type="date" min={toLocalISO(new Date())} aria-invalid={!!errors.preferred_date} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
                      {(errors.preferred_date || tuesdayNote) && (
                        <p data-testid="booking-date-error" className={errCls}>
                          {errors.preferred_date || "We're closed on Tuesdays — please choose another day."}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="bk-service" className={labelCls}>Service<Req /></label>
                    <select id="bk-service" data-testid="booking-service-select" aria-invalid={!!errors.service} value={form.service} onChange={set("service")} className={`${inputCls} ${form.service ? "" : "text-deep/40"}`}>
                      <option value="" disabled>Select a service</option>
                      {SERVICE_GROUPS.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.options.map((o) => (
                            <option key={o}>{o}</option>
                          ))}
                        </optgroup>
                      ))}
                      <option>Not sure — please advise</option>
                    </select>
                    {errors.service && <p data-testid="booking-service-error" className={errCls}>{errors.service}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-message" className={labelCls}>Anything We Should Know</label>
                    <textarea id="bk-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Goals, history, sensitivities — the more we know, the better your first visit." className={inputCls} />
                  </div>
                  {/* Honeypot — Web3Forms "botcheck": a checkbox hidden off-screen that
                      real visitors never check. No visual change to the form. */}
                  <input
                    ref={botcheckRef}
                    type="checkbox"
                    name="botcheck"
                    tabIndex={-1}
                    aria-hidden="true"
                    autoComplete="off"
                    className="absolute -left-[9999px] w-0 h-0 opacity-0"
                  />
                  <button
                    type="submit"
                    data-testid="booking-submit-button"
                    disabled={sending}
                    className="font-sans uppercase tracking-[0.2em] text-xs font-medium px-10 py-4 bg-gold border border-gold text-espresso hover:bg-champagne hover:border-champagne transition-all duration-300 disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Request Appointment"}
                  </button>
                  {sendFailed && (
                    <p data-testid="booking-error-message" className="font-sans text-sm tracking-wide text-[#9C6B3D]">
                      Something went wrong while sending your request — please call us at {PHONE} or{" "}
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4 hover:text-gold transition-colors"
                      >
                        book online
                      </a>.
                    </p>
                  )}
                  <p className="text-deep/50 text-xs tracking-wide">
                    We'll reach out to confirm your appointment.
                  </p>
                </form>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-espresso text-ivory p-10 sm:p-12 img-frame" data-testid="contact-info-panel">
                <h3 className="font-display text-3xl">The Studio</h3>
                <div className="mt-8 space-y-6">
                  <p className="flex items-start gap-4 text-ivory/75 text-sm leading-relaxed">
                    <MapPin size={17} strokeWidth={1.25} className="text-gold mt-0.5 shrink-0" />
                    {ADDRESS}
                  </p>
                  <a
                    href={`tel:${PHONE_TEL}`}
                    data-testid="contact-phone-link"
                    className="flex items-center gap-4 text-ivory/75 text-sm hover:text-champagne transition-colors duration-300"
                  >
                    <Phone size={17} strokeWidth={1.25} className="text-gold shrink-0" />
                    {PHONE}
                  </a>
                  <div className="flex items-start gap-4">
                    <Clock size={17} strokeWidth={1.25} className="text-gold mt-0.5 shrink-0" />
                    <ul className="space-y-2 flex-1">
                      {HOURS.map((h) => (
                        <li key={h.day} className="flex justify-between font-sans uppercase tracking-[0.15em] text-[11px] text-ivory/60">
                          <span>{h.day}</span>
                          <span className="text-champagne/90">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-10 pt-8 border-t border-ivory/10">
                  <p className="font-quote italic text-lg text-ivory/70">
                    "One client at a time. Never a queue."
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25} className="mt-8">
              <div className="border border-stone img-frame overflow-hidden" data-testid="contact-map">
                <iframe
                  title="S & Z Hair and Laser — 70 Chesterwood Cres Unit A, Brampton"
                  src="https://www.google.com/maps?q=70%20Chesterwood%20Cres%20Unit%20A%2C%20Brampton%2C%20ON%20L6Y%200Z4&output=embed"
                  className="w-full h-72 grayscale-[35%] contrast-[1.02]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
